import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import DataTable from "../../components/common/DataTable";
import DatePicker from "../../components/common/DatePicker";
import Select from "../../components/common/Select";
import { getTransactions } from "../../services/transactionService";
import api from "../../services/api";

// Helper functions
const toNumber = (value) => {
  const num = parseFloat(value);
  return isNaN(num) ? 0 : num;
};

const formatCurrency = (amount) => {
  const num = toNumber(amount);
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num);
};

const statusStyle = (status) => {
  const s = String(status || "").toUpperCase();
  if (s === "SUCCESS") return "bg-emerald-100 text-emerald-700";
  if (s === "PENDING") return "bg-amber-100 text-amber-600";
  if (s === "FAILED") return "bg-red-100 text-red-600";
  if (s === "REFUNDED") return "bg-blue-100 text-blue-600";
  return "bg-gray-100 text-gray-600";
};

export default function MyService() {
  const [serviceTab, setServiceTab] = useState("");
  const [myServices, setMyServices] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [wallet, setWallet] = useState(null);
  
  // Filter states
  const [searchText, setSearchText] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateError, setDateError] = useState('');
  
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [exporting, setExporting] = useState(false);
  const [showFormatMenu, setShowFormatMenu] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowFormatMenu(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Status options
  const statusOptions = [
    { label: "All", value: "" },
    { label: "Success", value: "success" },
    { label: "Pending", value: "pending" },
    { label: "Failed", value: "failed" },
    { label: "Refunded", value: "refunded" },
  ];

  // Validate dates
  const validateDates = (from, to) => {
    if (from && to && new Date(from) > new Date(to)) {
      setDateError('From date cannot be after To date.');
    } else {
      setDateError('');
    }
  };

  const handleExport = async (format = 'csv') => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (searchText) params.append('search', searchText);
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      if (statusFilter) params.append('status', statusFilter);
      if (serviceTab) params.append('service_id', serviceTab);
      params.append('format', format);
      params.append('limit', 10000);
      params.append('page', 1);

      const response = await api.get(`/transactions/export?${params.toString()}`, { responseType: 'blob' });

      const contentType = response.headers?.['content-type'] || '';
      if (contentType.includes('application/json')) {
        const text = await response.data.text();
        const json = JSON.parse(text);
        toast.info(json.message || 'Export queued. Check Reports section to download.');
        return;
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const ext = format === 'excel' ? 'xlsx' : 'csv';
      const timestamp = new Date().toISOString().split('T')[0];
      const serviceName = myServices.find(s => s.service_id === serviceTab)?.name || 'transactions';
      let filename = `${serviceName}_${timestamp}`;
      if (fromDate) filename += `_from_${fromDate}`;
      if (toDate) filename += `_to_${toDate}`;
      if (statusFilter) filename += `_${statusFilter}`;
      filename += `.${ext}`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export completed successfully!');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Fetch wallet balance
  useEffect(() => {
    api.get("/wallet/balance")
      .then((res) => setWallet(res.data?.data || null))
      .catch(() => {});
  }, []);

  // Fetch user's services
  useEffect(() => {
    api.get("/services/my")
      .then((res) => {
        const services = res.data?.data || res.data || [];
        setMyServices(Array.isArray(services) ? services : []);
        if (services.length > 0) setServiceTab(services[0].service_id);
      })
      .catch(() => {});
  }, []);

  // Debounced search - reset to page 1 when filters change
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText, fromDate, toDate, statusFilter]);

  // Fetch transactions
  const fetchTransactions = useCallback(async () => {
    if (!serviceTab) return;
    
    setLoading(true);
    try {
      const params = {
        page: page,
        limit: limit,
        ...(searchText && { search: searchText }),
        ...(serviceTab && { service_id: serviceTab }),
        ...(fromDate && { from_date: fromDate }),
        ...(toDate && { to_date: toDate }),
        ...(statusFilter && { status: statusFilter }),
      };
      
      const res = await getTransactions(params);
      
      const responseData = res.data?.data || res.data || {};
      const rows = Array.isArray(responseData?.transactions) 
        ? responseData.transactions 
        : Array.isArray(responseData?.data) 
          ? responseData.data 
          : Array.isArray(responseData) 
            ? responseData 
            : [];
      
      setTransactions(rows);
      
      const p = responseData?.pagination || {};
      setPagination({
        current_page: Number(p.current_page) || page,
        last_page: Number(p.last_page) || 1,
        total: Number(p.total) || rows.length
      });
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch transactions");
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchText, serviceTab, fromDate, toDate, statusFilter]);

  // Fetch when dependencies change
  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const handleLimitChange = (newLimit) => {
    setLimit(Number(newLimit));
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.last_page) {
      setPage(newPage);
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchText('');
    setFromDate('');
    setToDate('');
    setStatusFilter('');
    setDateError('');
    setPage(1);
  };

  // Columns definition
  const columns = [
    {
      id: "sr",
      header: "Sr No",
      size: 60,
      Cell: ({ row }) => (
        <span className="text-[13px] text-[#4C444E] font-semibold">
          {(page - 1) * limit + row.index + 1}
        </span>
      ),
    },
    {
      accessorKey: "transaction_id",
      header: "Transaction ID",
      size: 260,
      Cell: ({ row }) => (
        <span className="text-[#4B2364] font-semibold text-[12px] font-mono">
          {row.original.transaction_id || row.original.id || "—"}
        </span>
      ),
    },
    {
      accessorKey: "created_at",
      header: "Date & Time",
      size: 140,
      Cell: ({ row }) => {
        const d = row.original.created_at ? new Date(row.original.created_at) : null;
        return (
          <div className="text-gray-600 text-xs whitespace-nowrap">
            {d ? d.toLocaleDateString('en-IN') : "—"}<br />
            <span className="text-gray-400">{d ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "utr",
      header: "UTR",
      Cell: ({ row }) => <span className="text-sm text-gray-800 font-medium">{row.original.utr || "—"}</span>,
    },
    {
      accessorKey: "ip_address",
      header: "IP Address",
      Cell: ({ row }) => <span className="text-sm text-gray-800 font-medium">{row.original.ip_address || "—"}</span>,
    },
    {
      accessorKey: "service",
      header: "Service Type",
      Cell: ({ row }) => {
        const serviceName = row.original.service?.name || row.original.service_name || "";
        return (
          <span className="text-sm text-gray-800 font-semibold">{serviceName || "—"}</span>
        );
      },
    },
    {
      accessorKey: "amount",
      header: "Amount",
      size: 100,
      Cell: ({ row }) => (
        <span className="text-gray-900 font-bold text-sm">
          {row.original.amount != null ? formatCurrency(row.original.amount) : "—"}
        </span>
      ),
    },
    {
      accessorKey: "commission",
      header: "Commission/Charges",
      Cell: ({ row }) => {
        const commission = toNumber(row.original.commission ?? 0);
        const isZero = commission === 0;
        const color = isZero ? "text-gray-400" : "text-red-500";
        const prefix = isZero ? "" : "- ";
        
        return (
          <span className={`text-[13px] font-bold ${color}`}>
            {prefix}{formatCurrency(commission)}
          </span>
        );
      },
    },
    {
      accessorKey: "net_amount",
      header: "Net Amount",
      Cell: ({ row }) => {
        const netAmount = row.original.net_amount != null 
          ? toNumber(row.original.net_amount)
          : (toNumber(row.original.amount) - toNumber(row.original.commission));
        
        return (
          <span className="text-emerald-600 font-bold text-sm">
            {netAmount > 0 ? formatCurrency(netAmount) : "—"}
          </span>
        );
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      size: 120,
      Cell: ({ row }) => {
        const status = (row.original.status || "").toLowerCase();
        let cls = "bg-gray-100 text-gray-600";
        
        if (status === "success") {
          cls = "bg-emerald-100 text-emerald-700";
        } else if (status === "failed" || status === "failure") {
          cls = "bg-red-100 text-red-700";
        } else if (status === "pending") {
          cls = "bg-amber-100 text-amber-600";
        } else if (status === "refunded") {
          cls = "bg-blue-100 text-blue-600";
        }
        
        return (
          <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide ${cls}`}>
            {status || "—"}
          </span>
        );
      },
    },
  ];

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 p-5">
      <div className="max-w-7xl mx-auto space-y-4">

        {/* Top bar */}
        <div className="flex items-start flex-wrap gap-4 justify-between">
          <div>
            <h1 className="text-[36px] font-bold text-primary-mid">Service Wise Ledger Report</h1>
            <p className="text-[14px] text-[#6B6471] mt-1">View and manage transactions for your services</p>
          </div>
          <div className="flex gap-1 bg-[#EDEEEF] rounded-[8px] p-1">
            {myServices.map(s => (
              <button 
                key={s.service_id} 
                onClick={() => {
                  setServiceTab(s.service_id);
                  setPage(1);
                }}
                className={`px-4 py-1.5 border-none rounded-md text-sm font-semibold transition-all cursor-pointer ${
                  serviceTab === s.service_id 
                    ? "bg-white shadow-[0px_1px_2px_0px_#0000000D] text-primary-mid" 
                    : "bg-transparent text-gray-500 hover:bg-gray-100"
                }`}
              >
                {s.name}
              </button>
            ))}
          </div>
        </div>

        {/* Wallet Balance Card */}
        <div className="bg-primary-mid rounded-[24px] p-[24px] shadow-[0px_8px_10px_-6px_#0000001A,0px_20px_25px_-5px_#0000001A] flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 bg-white/20 rounded-[16px] flex items-center justify-center">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2}>
                  <rect x="2" y="5" width="20" height="14" rx="2"/>
                  <path d="M2 10h20"/>
                  <circle cx="7" cy="15" r="1" fill="#fff"/>
                </svg>
              </div>
              <span className="text-[12px] font-semibold text-[#FFFFFF99] uppercase tracking-wider">Wallet Balance</span>
            </div>
            <div className="text-[36px] font-bold text-white">
              {formatCurrency(wallet?.available_balance || 0)}
            </div>
            <div className="text-[13px] text-[#FFFFFFCC] mt-1">
              Total: {formatCurrency(wallet?.total_balance || 0)} &nbsp;|&nbsp; 
              Hold: {formatCurrency(wallet?.hold_balance || 0)}
            </div>
          </div>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5">
            <rect x="2" y="5" width="20" height="14" rx="2"/>
            <path d="M2 10h20"/>
          </svg>
        </div>

        {/* Filter Section */}
        <div className="px-6 py-5 bg-[#F3F4F5] rounded-[16px] border border-[#CEC3D01A]">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2" width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="#9ca3af" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="M21 21l-4.35-4.35" strokeLinecap="round"/>
              </svg>
              <input
                type="text"
                placeholder="Search by UTR or ID..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="bg-white rounded-[12px] pl-8 pr-4 py-2.5 text-[13px] text-[#191C1D] border-none outline-none focus:ring-2 focus:ring-primary-mid/30 w-64 transition"
              />
            </div>

            {/* From Date */}
            <div className={`flex items-center gap-3 py-2 px-4 rounded-[12px] bg-white ring-1 transition-all ${dateError ? 'ring-red-400' : 'ring-transparent'}`}>
              <span className="text-[12px] font-semibold text-[#4C444E] uppercase">FROM</span>
              <DatePicker
                value={fromDate}
                onChange={(e) => { setFromDate(e.target.value); validateDates(e.target.value, toDate); }}
                placeholder="dd/mm/yyyy"
                className="w-[110px]"
              />
            </div>

            {/* To Date */}
            <div className={`flex items-center gap-3 py-2 px-4 rounded-[12px] bg-white ring-1 transition-all ${dateError ? 'ring-red-400' : 'ring-transparent'}`}>
              <span className="text-[12px] font-semibold text-[#4C444E] uppercase">TO</span>
              <DatePicker
                value={toDate}
                onChange={(e) => { setToDate(e.target.value); validateDates(fromDate, e.target.value); }}
                placeholder="dd/mm/yyyy"
                className="w-[110px]"
              />
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-3 py-2 px-4 rounded-[12px] bg-white min-w-[150px]">
              <span className="text-[12px] font-semibold text-[#4C444E] uppercase">STATUS</span>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="min-w-[100px]"
                options={statusOptions}
              />
            </div>

            {/* Clear Filters Button */}
            {(searchText || fromDate || toDate || statusFilter) && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1.5 text-gray-500 text-[12px] hover:text-gray-700 transition-colors"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round"/>
                </svg>
                Clear
              </button>
            )}

            {/* Export Split Button */}
            <div className="relative ml-auto" ref={menuRef}>
              <div className={`flex items-center rounded-lg overflow-hidden ${exporting ? 'opacity-60 pointer-events-none' : ''}`}>
                <button
                  onClick={() => handleExport('csv')}
                  disabled={exporting || transactions.length === 0}
                  className="flex items-center gap-2 bg-[#4B2364] px-4 py-[9px] text-[13px] font-semibold text-white cursor-pointer border-none transition-colors disabled:opacity-60"
                >
                  {exporting ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                  )}
                  {exporting ? 'Exporting...' : 'Export'}
                </button>
                <button
                  onClick={() => setShowFormatMenu((v) => !v)}
                  disabled={exporting || transactions.length === 0}
                  className="flex items-center justify-center bg-[#4B2364] px-2.5 h-[37px] text-white border-none cursor-pointer transition-colors disabled:opacity-60"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className={`transition-transform duration-200 ${showFormatMenu ? 'rotate-180' : ''}`}>
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
              </div>

              {showFormatMenu && (
                <div className="absolute right-0 top-full mt-2 z-50 bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.12)] border border-gray-100 overflow-hidden w-[160px]">
                  <div className="px-3 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-100">
                    Choose Format
                  </div>
                  <button
                    onClick={() => { handleExport('csv'); setShowFormatMenu(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-[13px] font-medium text-gray-700 hover:bg-gray-50 cursor-pointer border-none bg-transparent transition-colors"
                  >
                    <span className="w-6 h-6 rounded-md bg-emerald-50 flex items-center justify-center shrink-0">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14,2 14,8 20,8" />
                        <line x1="8" y1="13" x2="16" y2="13" />
                        <line x1="8" y1="17" x2="16" y2="17" />
                      </svg>
                    </span>
                    CSV
                  </button>
                  <button
                    onClick={() => { handleExport('excel'); setShowFormatMenu(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-[13px] font-medium text-gray-700 hover:bg-gray-50 cursor-pointer border-none bg-transparent transition-colors"
                  >
                    <span className="w-6 h-6 rounded-md bg-blue-50 flex items-center justify-center shrink-0">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14,2 14,8 20,8" />
                        <rect x="8" y="12" width="8" height="6" rx="1" />
                      </svg>
                    </span>
                    Excel
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Date Error Message */}
          {dateError && (
            <div className="mt-3 flex items-center gap-2 text-red-500 text-[11px] font-medium">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              {dateError}
            </div>
          )}

          {/* Active Filters Display */}
          {(searchText || fromDate || toDate || statusFilter) && !dateError && (
            <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-gray-200">
              <span className="text-[11px] text-gray-500 font-medium">Active Filters:</span>
              {searchText && (
                <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-1 rounded-full flex items-center gap-1">
                  Search: {searchText}
                  <button onClick={() => setSearchText('')} className="hover:text-blue-900">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round"/>
                    </svg>
                  </button>
                </span>
              )}
              {fromDate && (
                <span className="text-[10px] bg-green-100 text-green-700 px-2 py-1 rounded-full flex items-center gap-1">
                  From: {fromDate}
                  <button onClick={() => setFromDate('')} className="hover:text-green-900">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round"/>
                    </svg>
                  </button>
                </span>
              )}
              {toDate && (
                <span className="text-[10px] bg-green-100 text-green-700 px-2 py-1 rounded-full flex items-center gap-1">
                  To: {toDate}
                  <button onClick={() => setToDate('')} className="hover:text-green-900">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round"/>
                    </svg>
                  </button>
                </span>
              )}
              {statusFilter && (
                <span className="text-[10px] bg-purple-100 text-purple-700 px-2 py-1 rounded-full flex items-center gap-1">
                  Status: {statusFilter === "success" ? "Success" : statusFilter === "pending" ? "Pending" : statusFilter === "failed" ? "Failed" : "Refunded"}
                  <button onClick={() => setStatusFilter('')} className="hover:text-purple-900">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round"/>
                    </svg>
                  </button>
                </span>
              )}
            </div>
          )}
        </div>     

        {/* Table */}
        <div className="bg-white rounded-[32px] border border-[#CEC3D01A] shadow-[0px_20px_40px_0px_#4B236408] overflow-hidden">
          <div className="text-primary-mid p-[20px] border-b flex justify-between items-center border-[#EDEEEF]">
            <div>
              <p className="text-[18px] font-bold">Transactions ledger</p>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={limit}
                onChange={(e) => handleLimitChange(e.target.value)}
                className="bg-[#F3F4F5] rounded-[10px] px-3 py-2 text-[13px] text-[#191C1D] border-none outline-none focus:ring-2 focus:ring-[#4B2364]/30 cursor-pointer"
              >
                <option value="10">10 / page</option>
                <option value="25">25 / page</option>
                <option value="50">50 / page</option>
                <option value="100">100 / page</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-2 border-primary-mid border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              <DataTable columns={columns} data={transactions} />
              {transactions.length === 0 && !loading && (
                <div className="text-center py-10 text-gray-400 text-sm">
                  No transactions found.
                  {(searchText || fromDate || toDate || statusFilter) && (
                    <button onClick={clearFilters} className="block mx-auto mt-2 text-primary-mid text-xs underline">
                      Clear filters
                    </button>
                  )}
                </div>
              )}
            </>
          )}

          {/* Pagination */}
          {pagination.total > 0 && (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-6 py-4 border-t border-gray-100 gap-3">
              <span className="text-xs text-[#4C444E]">
                Showing {(page - 1) * limit + 1}–{Math.min(page * limit, pagination.total)} of {pagination.total} records
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                  className={`w-8 h-8 flex items-center justify-center rounded-lg border border-[#CEC3D0] ${page === 1 ? 'bg-gray-50 text-gray-300 cursor-not-allowed' : 'bg-white text-[#4C444E] hover:bg-gray-50 cursor-pointer'}`}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
                <span className="text-xs text-[#4C444E]">
                  Page {pagination.current_page} of {pagination.last_page}
                </span>
                <button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === pagination.last_page}
                  className={`w-8 h-8 flex items-center justify-center rounded-lg border border-[#CEC3D0] ${page === pagination.last_page ? 'bg-gray-50 text-gray-300 cursor-not-allowed' : 'bg-white text-[#4C444E] hover:bg-gray-50 cursor-pointer'}`}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 18l6-6-6-6" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}